import isEqual from 'react-fast-compare';
import {createSelector} from 'reselect';
import {
  SOURCE_AMBIENT_WEATHER,
  SOURCE_DAVIS,
  SOURCE_ECOWITT,
  SOURCE_MY_ACURITE,
  SOURCE_NETATMO_WEATHER,
  SOURCE_SYNOP,
  SOURCE_WEATHERFLOW,
  SOURCE_WUNDERGROUND,
} from '../../api';
import {StationSource} from '../../api/types';
import {Forecast, Station} from '../../utils';
import {NormalizedForecastModel} from '../../utils/Forecast/types';
import {selectIsPremium} from '../billing/selectors';
import {deriveForecastSelection} from '../forecastModel/deriveForecastSelection';
import {resolveSelectedForecastModel} from '../forecastModel/resolveSelectedForecastModel';
import {CommonRootState} from '../types';
import {DashboardStationConfig, netatmoDashboardKey} from '.';
import {
  selectForecastModelMode,
  selectForecastModelOrder,
  selectForecastModelPins,
  selectForecastModelStrategy,
} from '../userPreference/selectors';

const EMPTY_DASHBOARD_ID_LIST: string[] = [];
const EMPTY_DASHBOARD_CONFIG: NonNullable<
  CommonRootState['weather']['dashboardConfig']
> = {};
const DEFAULT_MAP_SOURCE_FILTER: StationSource[] = [
  SOURCE_SYNOP,
  SOURCE_NETATMO_WEATHER,
  SOURCE_DAVIS,
  SOURCE_WUNDERGROUND,
  SOURCE_AMBIENT_WEATHER,
  SOURCE_ECOWITT,
  SOURCE_MY_ACURITE,
  SOURCE_WEATHERFLOW,
];
const EMPTY_FORECAST_MODEL_LIST: NormalizedForecastModel[] = [];

export const getStationList = (state: CommonRootState) =>
  state.weather.stationList;

const getStationSort = (state: CommonRootState) => state.weather.stationSort;

// Stations in the server-provided order. An id stationSort does not know
// ranks LAST (in stationList order): indexOf() === -1 used to sort it first,
// so a stale persisted entry outranked every live one, and uid lookups such
// as netatmoWeather's getGenericStationByUid resolved to it (#285).
// Retired stations (gone from their provider listing, no new data) sink below
// every live one, each group keeping that order (#324).
export const getSortedStationList = createSelector(
  getStationList,
  getStationSort,
  (stationList, stationSort) => {
    const rank = (id: number) => {
      const index = stationSort.indexOf(id);
      return index === -1 ? Number.MAX_SAFE_INTEGER : index;
    };
    return stationList
      .slice()
      .sort(
        (a, b) =>
          Number(Station.isStationRetired(a)) -
            Number(Station.isStationRetired(b)) || rank(a.id) - rank(b.id),
      );
  },
);

export const getSortedVisibleStationList = createSelector(
  getSortedStationList,
  (sortedStationList) =>
    sortedStationList.filter((station) => !Station.isStationHidden(station)),
);

export const selectFavoriteStationIds = (state: CommonRootState) =>
  state.weather.favoriteStationIds;

/*
 * Retrieve ids from both stationList and favoriteIdList
 */
export const getUserStationIds = createSelector(
  getStationList,
  selectFavoriteStationIds,
  (stationList, favoriteIds) => {
    const fullList = stationList.map((s) => s.id).concat(favoriteIds);
    return fullList.filter((item, index) => fullList.indexOf(item) === index);
  },
);

export const getRawCurrentStationId = (state: CommonRootState) =>
  state.weather.currentStation;

export const getCurrentPublicStationId = createSelector(
  getRawCurrentStationId,
  selectFavoriteStationIds,
  getSortedStationList,
  (stationId, favoriteIds, stationList) => {
    if (stationId) {
      // An explicit choice stands, even a retired station.
      return stationId;
    }
    // A retired station gets no new data, so it is only the default when
    // nothing live is left (#324). A favorite id absent from stationList is
    // not known to be retired. The live-station step does not lean on the
    // sort putting live stations first.
    const retiredIds = new Set(
      stationList.filter(Station.isStationRetired).map((s) => s.id),
    );
    return (
      favoriteIds.find((id) => !retiredIds.has(id)) ??
      stationList.find((s) => !retiredIds.has(s.id))?.id ??
      favoriteIds[0] ??
      stationList[0]?.id ??
      null
    );
  },
);

export const getCurrentPublicStationFromList = createSelector(
  getCurrentPublicStationId,
  getStationList,
  (currentStationId, stationList) => {
    if (currentStationId) {
      return stationList.find((s) => s.id === currentStationId) || null;
    }
    return null;
  },
  // Keep a stable reference across polls that return identical content (#41),
  // so the dashboard station object — and the chart `data` it feeds — does not
  // change identity and force a Reanimated worklet re-serialization.
  {memoizeOptions: {resultEqualityCheck: isEqual}},
);

export const getId = (_: CommonRootState, id: number) => id;

export const getPublicStationFromList = createSelector(
  getStationList,
  getId,
  (stationList, id) => {
    if (id) {
      return stationList.find((s) => s.id === id) || null;
    }
    return null;
  },
);

export const getStationMapSourceFilter = (state: CommonRootState) =>
  (state.weather.stationMapFilter && state.weather.stationMapFilter.source) ||
  DEFAULT_MAP_SOURCE_FILTER;

export const getMapMetric = (state: CommonRootState) =>
  (state.weather.stationMapFilter && state.weather.stationMapFilter.metric) ||
  'T';

export const selectWeatherChartConfig = (state: CommonRootState) =>
  state.weather.chartConfig;

/*
Dashboard Customization
*/
export const selectDashboardConfig = (state: CommonRootState) =>
  state.weather.dashboardConfig || EMPTY_DASHBOARD_CONFIG;

export const getDashboardOrder = createSelector(
  selectDashboardConfig,
  getId,
  (config, id) => (config[id] ? config[id].order : EMPTY_DASHBOARD_ID_LIST),
);

export const getDashboardHidden = createSelector(
  selectDashboardConfig,
  getId,
  (config, id) => (config[id] ? config[id].hidden : EMPTY_DASHBOARD_ID_LIST),
);

const EMPTY_NETATMO_MODULE_LAYOUT: DashboardStationConfig = {
  order: EMPTY_DASHBOARD_ID_LIST,
  hidden: EMPTY_DASHBOARD_ID_LIST,
};

/**
 * A Netatmo station's saved module layout (`order` / `hidden` of module ids),
 * or a stable empty layout when none is saved. Either list may be missing on
 * an entry hydrated from the server, so each falls back on its own.
 */
export const getNetatmoModuleLayout = createSelector(
  selectDashboardConfig,
  (_: CommonRootState, netatmoStationId: string) => netatmoStationId,
  (config, netatmoStationId): DashboardStationConfig => {
    const entry = config[netatmoDashboardKey(netatmoStationId)];
    if (!entry) {
      return EMPTY_NETATMO_MODULE_LAYOUT;
    }
    if (entry.order && entry.hidden) {
      // The stored object itself, so an edit to another key of
      // `dashboardConfig` leaves every shell's memo warm.
      return entry;
    }
    return {
      order: entry.order ?? EMPTY_DASHBOARD_ID_LIST,
      hidden: entry.hidden ?? EMPTY_DASHBOARD_ID_LIST,
    };
  },
);

/*
Weather Forecast
*/
const getRawForecastModels = (state: CommonRootState) => {
  return state.weather.forecastModels;
};

export const getForecastModelsByGenericStationId = createSelector(
  getRawForecastModels,
  selectForecastModelOrder,
  getId,
  (forecastModels, savedOrder, id) => {
    if (id in forecastModels) {
      const list = Forecast.ForecastUtils.getForecastModelList(
        forecastModels[id],
      ).getList();
      return Forecast.ForecastUtils.orderForecastModelsByPreference(
        list,
        savedOrder,
      );
    }
    return EMPTY_FORECAST_MODEL_LIST;
  },
);

const getRawSelectedForecastModel = (state: CommonRootState) => {
  return state.weather.selectedForecastModel;
};

/**
 * Stands in for the override dictionary when resolving what the *preference*
 * alone would serve. Shared and frozen so the resolve is fed one stable empty
 * object rather than a fresh literal per call.
 */
const NO_OVERRIDES: Record<number, string> = Object.freeze({});

export const getSelectedForecastModelByGenericStationId = createSelector(
  getForecastModelsByGenericStationId,
  getRawSelectedForecastModel,
  selectForecastModelOrder,
  getId,
  selectIsPremium,
  resolveSelectedForecastModel,
);

/**
 * Why this station shows the model it shows — see `deriveForecastSelection`.
 *
 * The picker needs the reason, not just the model: `fallback` and
 * `noPreference` resolve identically today, so a screen comparing models
 * cannot tell "your preference did not reach here" from "you have no
 * preference". Derived here rather than in each app so both branch on one
 * answer.
 */
export const getForecastSelectionByGenericStationId = createSelector(
  getSelectedForecastModelByGenericStationId,
  getForecastModelsByGenericStationId,
  getRawSelectedForecastModel,
  getId,
  selectForecastModelPins,
  selectForecastModelMode,
  selectForecastModelStrategy,
  selectIsPremium,
  selectForecastModelOrder,
  (resolved, list, overrides, id, pins, mode, strategy, isPremium, order) =>
    deriveForecastSelection({
      resolved,
      // The same resolve, with the override dictionary emptied: what the
      // profile preference alone serves here. Cheap — a walk over at most a
      // dozen models — and there is no other way to name what "use my
      // preferred models" would give you while an override is in force.
      preferred: resolveSelectedForecastModel(
        list,
        NO_OVERRIDES,
        order,
        id,
        isPremium,
      ),
      isOverridden: id in overrides,
      pins,
      available: list.map((item) => item.serverId.forecastModel),
      hasPreference: Forecast.hasForecastPreference(pins, mode, strategy),
      // The same predicate the resolver two selectors up is fed. Anything else
      // lets the strip describe a model the resolver refuses to serve.
      isPremium,
    }),
);
